import { api } from '../api/client'
import type { SyncCommand, SyncCommandsResponse } from '../api/types'
import { getDeviceId } from './device'

/*
  The last of the offline outbox (ADR 0020).

  Until 2026-10-23 a record made with no signal waited in the browser's IndexedDB database
  `tally4244`, store `outbox`, until a sync cycle sent it. A browser that held such records
  when the PWA was retired would lose them if the database were simply dropped. So, once per
  page load after sign-in, this looks for that database; if it is there, every command still
  marked pending is sent (the server applies each once by its commandId, so a command that did
  reach the server before is answered Duplicate), and when they have all been answered the
  database is deleted. A failure leaves it in place for the next load.

  Plain IndexedDB, not the `idb` package, which went with the offline layer. Delete this file
  once no browser can still hold the old database (a year is ample).
*/

const DB_NAME = 'tally4244'

interface LegacyRecord extends SyncCommand {
  status: string
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function exists(): Promise<boolean> {
  if (typeof indexedDB === 'undefined') return false
  // databases() is missing in older Firefox; there, opening is the only test, and an open
  // that has to create the database is aborted below so nothing is left behind.
  if (typeof indexedDB.databases === 'function') {
    return (await indexedDB.databases()).some((d) => d.name === DB_NAME)
  }
  return true
}

async function readPending(): Promise<LegacyRecord[] | null> {
  const db = await new Promise<IDBDatabase | null>((resolve, reject) => {
    const open = indexedDB.open(DB_NAME)
    open.onupgradeneeded = () => {
      // There was no database: do not create one.
      open.transaction?.abort()
      resolve(null)
    }
    open.onsuccess = () => resolve(open.result)
    open.onerror = () => (open.error?.name === 'AbortError' ? resolve(null) : reject(open.error))
  })
  if (!db) return null
  try {
    if (!db.objectStoreNames.contains('outbox')) return []
    const all = await request(
      db.transaction('outbox', 'readonly').objectStore('outbox').getAll() as IDBRequest<
        LegacyRecord[]
      >,
    )
    return all
      .filter((r) => r.status === 'pending')
      .sort((a, b) => (a.commandId < b.commandId ? -1 : 1))
  } finally {
    db.close()
  }
}

function deleteDatabase(): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME)
    req.onsuccess = () => resolve()
    req.onerror = () => resolve()
    req.onblocked = () => resolve()
  })
}

let ran = false

/** Sends what the old outbox still held, then deletes the old database. Once per page load. */
export async function drainLegacyOutbox(): Promise<void> {
  if (ran) return
  ran = true
  try {
    if (!(await exists())) return
    const pending = await readPending()
    if (pending === null) return

    // The API requires the scope headers to match each command, so send per ranch.
    const byScope = new Map<string, LegacyRecord[]>()
    for (const r of pending) {
      const key = `${r.organizationId}/${r.ranchId}`
      byScope.set(key, [...(byScope.get(key) ?? []), r])
    }
    let refused = 0
    for (const records of byScope.values()) {
      const first = records[0]!
      for (let i = 0; i < records.length; i += 50) {
        const commands = records.slice(i, i + 50).map((r) => ({
          commandId: r.commandId,
          organizationId: r.organizationId,
          ranchId: r.ranchId,
          type: r.type,
          payload: r.payload,
          occurredAt: r.occurredAt,
          recordedAt: r.recordedAt,
          gps: r.gps ?? null,
        }))
        const { data } = await api.post<SyncCommandsResponse>(
          '/api/v1/sync/commands',
          { deviceId: getDeviceId(), commands },
          { scope: { organizationId: first.organizationId, ranchId: first.ranchId } },
        )
        refused += (data.results ?? []).filter(
          (r) => r.outcome === 'Rejected' || r.outcome === 'Conflict',
        ).length
      }
    }
    if (pending.length > 0) {
      console.info(
        `Sent ${pending.length} record(s) left from the offline app` +
          (refused ? `; the server refused ${refused}.` : '.'),
      )
    }
    await deleteDatabase()
  } catch (error) {
    // Not reachable, or not signed in to that ranch: try again on the next load.
    console.warn('Records left from the offline app could not be sent yet.', error)
    ran = false
  }
}
